import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { obterDadosBasicosPrincipais, obterDadosBasicosSecundarios, obterProgramaSocialPorId } from '../../api/programasSociaisAPI';
import { RootState } from '../../app/store';
import {
  MSG_ERRO_VERSAO_MINIMA_NAO_ATENDIDA,
  MSG_ERRO_VERSAO_MINIMA_NAO_ATENDIDA_WEB,
} from '../../common/Mensagens';


interface ProgramaSocialBasico {
  idPrograma: string,
  nomeProgramaSocial: string;
  logoBase64?: string | null;
  nmProgramaSocialReduzido?: string;
  inProgramaSocialReduzido: number;
}

interface ProgramaSocialCompleto extends ProgramaSocialBasico {
  definicao: string;
  publicoAlvo: string;
  comoSolicitar: string;
  orgaoResponsavel: string;
  indicadorPrincipal?: number;
  imagemBase64?: string | null;
  maisInfo: string[];
}

export interface ProgramasSociaisState {
  principais: ProgramaSocialBasico[];
  secundarios: ProgramaSocialBasico[];
  programasCompletos: Record<string, ProgramaSocialCompleto>
  loading: boolean;
  loadingSecundarios: boolean;
  loadingDetalhes: Record<string, boolean>;
  secundariosCarregados: boolean;
  temComunicacaoComAPI: boolean;
  error: string | null;
}

const initialState: ProgramasSociaisState = {
  principais: [],
  secundarios: [],
  programasCompletos: {},
  loading: false,
  loadingSecundarios: false,
  loadingDetalhes: {},
  secundariosCarregados: false,
  temComunicacaoComAPI: true,
  error: null,
};

export const fetchDadosBasicosPrincipais = createAsyncThunk(
  'programasSociais/obterDadosBasicosPrincipais',
  async () => {
    return obterDadosBasicosPrincipais();
  }
)

export const fetchDadosBasicosSecundarios = createAsyncThunk(
  'programasSociais/obterdadosBasicosSecundarios',
  async () => {
    return obterDadosBasicosSecundarios();
  }
)

export const fetchProgramaSocialPorId = createAsyncThunk(
  'programasSociais/obterPorId',
  async (id: string) => {
    return obterProgramaSocialPorId(id);
  }
);

export const programasSociaisSlice = createSlice({
  name: 'programasSociais',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDadosBasicosPrincipais.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDadosBasicosPrincipais.fulfilled, (state, action) => {
        state.loading = false;
        state.principais = action.payload;
        state.temComunicacaoComAPI = true;
      })
      .addCase(fetchDadosBasicosSecundarios.pending, (state) => {
        state.loadingSecundarios = true;
      })
      .addCase(fetchDadosBasicosSecundarios.fulfilled, (state, action) => {
        state.loadingSecundarios = false;
        state.secundarios = action.payload;
        state.secundariosCarregados = true;
      })
      .addCase(fetchProgramaSocialPorId.pending, (state, action) => {
        const id = action.meta.arg;
        state.loadingDetalhes[id] = true;
      })
      .addCase(fetchProgramaSocialPorId.fulfilled, (state, action) => {
        const dadosComplementares = action.payload;
        const id = dadosComplementares.idPrograma;

        const programaBasico = state.principais.find(p => p.idPrograma === id) || state.secundarios.find(p => p.idPrograma === id);
        
        if(programaBasico) {
          state.programasCompletos[id] = {
            ...programaBasico,
            ...dadosComplementares
          }
        }

        state.loadingDetalhes[id] = false;
      })
      .addCase(fetchDadosBasicosPrincipais.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Erro ao carregar logos principais';
        if (
          !(
            action.error?.message &&
            (action.error?.message.includes(MSG_ERRO_VERSAO_MINIMA_NAO_ATENDIDA) ||
              action.error?.message.includes(MSG_ERRO_VERSAO_MINIMA_NAO_ATENDIDA_WEB))
          )
        ) {
          state.temComunicacaoComAPI = false;
        }
      })
      .addCase(fetchDadosBasicosSecundarios.rejected, (state, action) => {
        state.loadingSecundarios = false;
        state.error = action.error.message || 'Erro ao carregar logos secundários';
      })
      .addCase(fetchProgramaSocialPorId.rejected, (state, action) => {
        const id = action.meta.arg;
        state.loadingDetalhes[id] = false;
        state.error = action.error.message || 'Erro ao carregar detalhes do programa';
      });
  },
});

export const { clearError } = programasSociaisSlice.actions;

export const selectProgramasSociaisIsLoading = (state: RootState) => state.programasSociais.loading;
export const selectProgramasSociais = (state: RootState) => state.programasSociais.principais;
export const selectOutrosProgramas = (state: RootState) => state.programasSociais.secundarios;
export const selectProgramaCompleto = (id: string) => (state: RootState) => 
  state.programasSociais.programasCompletos[id];
export const selectIsLoadingDetalhes = (id: string) => (state: RootState) => 
  state.programasSociais.loadingDetalhes[id] || false;
export const selectProgramasSociaisError = (state: RootState) => state.programasSociais.error;
export const selectTemComunicacaoComAPI = (state: RootState) =>
  state.programasSociais.temComunicacaoComAPI;
export const selectLoadingSecundarios = (state: RootState) =>
  state.programasSociais.loadingSecundarios;
export const selectSecundariosCarregados = (state: RootState) =>
  state.programasSociais.secundariosCarregados;

export default programasSociaisSlice.reducer;
